// <elova-buy-now> — "Comprar agora" for blocks/elova-buy-now.liquid.
//
// Adds the selected variant with Horizon's own product-form data, then lands
// on the cart PAGE (routes.cart_url). Never /checkout, never Yampi directly:
// the cart page owns that step.
//
// Variant events (assets/events.js, dispatched by variant-picker.js, bubbling):
//   variant:selected — fires on click, BEFORE the new variant has loaded; the
//                      form still holds the old id, so we disable until…
//   variant:update   — fires once the new variant is in; re-apply the state.
// We listen where Horizon's product-form does: closest('.shopify-section').

class ElovaBuyNow extends HTMLElement {
  #busy = false;
  #pending = false;
  #available = true;
  #soldOut = false;

  connectedCallback() {
    this.section = this.closest('.shopify-section');
    this.button = this.querySelector('[data-buy-now-button]');
    this.errorEl = this.querySelector('[data-buy-now-error]');
    if (!this.section || !this.button || !this.errorEl || !this.#form()) return;

    this.#available = !this.button.disabled;
    this.#soldOut = this.button.textContent.trim() === this.dataset.labelSoldOut;

    this.button.addEventListener('click', this.#onClick);
    this.section.addEventListener('variant:selected', this.#onVariantSelected);
    this.section.addEventListener('variant:update', this.#onVariantUpdate);
    this.hidden = false;
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.#onClick);
    this.section?.removeEventListener('variant:selected', this.#onVariantSelected);
    this.section?.removeEventListener('variant:update', this.#onVariantUpdate);
  }

  #form() {
    const form = this.section?.querySelector(`#${CSS.escape(this.dataset.formId)}`);
    return form instanceof HTMLFormElement && form.dataset.type === 'add-to-cart-form' ? form : null;
  }

  #onVariantSelected = () => {
    this.#pending = true;
    this.#render();
  };

  #onVariantUpdate = (event) => {
    const detail = event.detail || {};
    const data = detail.data || {};
    if (data.newProduct) {
      this.dataset.productId = String(data.newProduct.id);
    } else if (String(data.productId) !== String(this.dataset.productId)) {
      return;
    }

    const variant = detail.resource;
    this.#pending = false;
    this.#available = Boolean(variant && variant.available);
    // Mirrors buy-buttons.liquid: an unavailable variant whose inventory Shopify
    // tracks is sold out; anything else unavailable is "Indisponível".
    this.#soldOut = Boolean(variant && !variant.available && variant.inventory_management);
    this.#clearError();
    this.#render();
  };

  #render() {
    const canBuy = this.#available && !this.#pending && !this.#busy;
    this.button.disabled = !canBuy;
    if (this.#busy || this.#available) {
      this.button.textContent = this.dataset.label;
    } else {
      this.button.textContent = this.#soldOut ? this.dataset.labelSoldOut : this.dataset.labelUnavailable;
    }
    if (this.#busy) this.button.setAttribute('aria-busy', 'true');
    else this.button.removeAttribute('aria-busy');
  }

  #onClick = async () => {
    if (this.#busy || this.#pending || !this.#available) return;
    const form = this.#form();
    if (!form) return;

    const body = new FormData(form);
    if (!body.get('id')) return;
    if (!body.get('quantity')) body.set('quantity', '1');

    this.#busy = true;
    this.#clearError();
    this.#render();

    try {
      const response = await fetch(this.dataset.cartAddUrl, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body,
      });
      if (!response.ok) {
        // Shopify's own, already-localised reason (e.g. a stock limit) is worth
        // showing; anything else falls back to the approved generic line.
        const payload = await response.json().catch(() => ({}));
        this.#fail(typeof payload.description === 'string' ? payload.description : '');
        return;
      }
      window.location.assign(this.dataset.cartUrl);
    } catch {
      // Network failure / abort: never surface the browser's raw (English) message.
      this.#fail('');
    }
  };

  #fail(message) {
    this.#busy = false;
    this.#render();
    this.#showError(message);
  }

  #showError(message) {
    this.errorEl.textContent = message || this.dataset.error || '';
  }

  #clearError() {
    this.errorEl.textContent = '';
  }
}

if (!customElements.get('elova-buy-now')) {
  customElements.define('elova-buy-now', ElovaBuyNow);
}
